import React, { useEffect, useState } from "react";
import { TestTube2, Phone, CreditCard, ArrowDown, ExternalLink, Copy, Check } from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || "/api";

type PhoneScenario = { suffix: string; behavior: string; description: string };
type CardScenario = { card: string; behavior: string; description: string };

interface ScenariosPayload {
  mobile_money?: {
    description: string;
    scenarios: PhoneScenario[];
    examples?: Record<string, string>;
  };
  card_payment?: {
    description: string;
    endpoint: string;
    scenarios: CardScenario[];
  };
  paydrc_collections?: {
    description: string;
    endpoint: string;
    scenarios: PhoneScenario[];
    examples?: Record<string, string>;
  };
}

function badgeColor(behavior: string): string {
  const b = behavior.toLowerCase();
  if (b.includes("success")) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (b.includes("decline") || b.includes("failed")) return "bg-rose-100 text-rose-800 border-rose-200";
  if (b.includes("pending")) return "bg-amber-100 text-amber-800 border-amber-200";
  if (b.includes("timeout") || b.includes("error")) return "bg-orange-100 text-orange-800 border-orange-200";
  if (b.includes("random")) return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-blue-100 text-blue-800 border-blue-200";
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="ml-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 transition"
      title="Copier"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export default function SandboxScenarios() {
  const [data, setData] = useState<ScenariosPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/sandbox/scenarios`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch(e => setError(String(e)));
  }, []);

  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
          Impossible de charger les scenarios: {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="animate-pulse rounded-xl border border-slate-200 bg-white p-8">
          <div className="h-6 w-1/3 bg-slate-200 rounded mb-4"></div>
          <div className="h-4 w-2/3 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 space-y-8">
      {/* Page header */}
      <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white p-6 shadow-lg">
        <div className="flex items-center gap-3">
          <TestTube2 className="h-8 w-8" />
          <div>
            <h1 className="text-2xl font-bold">Scenarios de test sandbox</h1>
            <p className="text-amber-50 text-sm mt-1">
              Numeros de telephone et de carte qui declenchent des comportements deterministes pour tester votre integration.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Money payouts */}
      {data.mobile_money && (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <header className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
            <ArrowDown className="h-5 w-5 text-emerald-600" />
            <div>
              <h2 className="font-semibold text-slate-900">Mobile Money — Payouts (marchand vers client)</h2>
              <p className="text-xs text-slate-600">{data.mobile_money.description}</p>
            </div>
          </header>
          <ScenarioTable scenarios={data.mobile_money.scenarios} examples={data.mobile_money.examples} kind="phone" />
        </section>
      )}

      {/* PayDRC collections */}
      {data.paydrc_collections && (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <header className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
            <Phone className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="font-semibold text-slate-900">Mobile Money — Collections (client vers marchand)</h2>
              <p className="text-xs text-slate-600">{data.paydrc_collections.description}</p>
              <code className="text-[11px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded mt-1 inline-block">
                POST {data.paydrc_collections.endpoint}
              </code>
            </div>
          </header>
          <ScenarioTable scenarios={data.paydrc_collections.scenarios} examples={data.paydrc_collections.examples} kind="phone" />
        </section>
      )}

      {/* Card payments */}
      {data.card_payment && (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <header className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-purple-600" />
            <div>
              <h2 className="font-semibold text-slate-900">Cartes — Cybersource</h2>
              <p className="text-xs text-slate-600">{data.card_payment.description}</p>
              <code className="text-[11px] text-purple-700 bg-purple-50 px-2 py-0.5 rounded mt-1 inline-block">
                POST {data.card_payment.endpoint}
              </code>
            </div>
          </header>
          <CardTable scenarios={data.card_payment.scenarios} />
        </section>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 flex items-start gap-2">
        <ExternalLink className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Ces scenarios sont activement maintenus pour le sandbox. Le comportement reel des switches peut differer
          (codes d&apos;erreur supplementaires, latences variables). La sandbox reproduit les flux principaux pour 95% des
          cas d&apos;integration.
        </span>
      </div>
    </div>
  );
}

function ScenarioTable({
  scenarios,
  examples,
  kind,
}: {
  scenarios: PhoneScenario[];
  examples?: Record<string, string>;
  kind: "phone";
}) {
  const exampleFor = (suffix: string) => {
    if (!examples) return null;
    const matches: Record<string, string[]> = {
      "0001": ["always_success"],
      "0002": ["pending_then_success"],
      "0003": ["insufficient_balance"],
      "0004": ["invalid_pin"],
      "0005": ["timeout"],
      "0006": ["pending_then_failure"],
      "0099": ["always_failed"],
    };
    const keys = matches[suffix] || [];
    for (const k of keys) {
      if (examples[k]) return examples[k];
    }
    return null;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white">
          <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
            <th className="text-left px-6 py-3 font-medium">Suffixe (4 derniers chiffres)</th>
            <th className="text-left px-6 py-3 font-medium">Exemple complet</th>
            <th className="text-left px-6 py-3 font-medium">Comportement</th>
            <th className="text-left px-6 py-3 font-medium">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {scenarios.map(s => {
            const example = exampleFor(s.suffix);
            return (
              <tr key={s.suffix} className="hover:bg-slate-50">
                <td className="px-6 py-3 font-mono text-slate-900">{s.suffix}</td>
                <td className="px-6 py-3 font-mono text-slate-700">
                  {example ? (
                    <span className="inline-flex items-center">
                      {example}
                      <CopyButton value={example} />
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-6 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${badgeColor(s.behavior)}`}>
                    {s.behavior}
                  </span>
                </td>
                <td className="px-6 py-3 text-slate-600">{s.description}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CardTable({ scenarios }: { scenarios: CardScenario[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white">
          <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
            <th className="text-left px-6 py-3 font-medium">Numero de carte</th>
            <th className="text-left px-6 py-3 font-medium">Comportement</th>
            <th className="text-left px-6 py-3 font-medium">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {scenarios.map((s, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="px-6 py-3 font-mono text-slate-900 inline-flex items-center">
                {s.card}
                {!s.card.toLowerCase().includes("other") && <CopyButton value={s.card.replace(/\s/g, "")} />}
              </td>
              <td className="px-6 py-3">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${badgeColor(s.behavior)}`}>
                  {s.behavior}
                </span>
              </td>
              <td className="px-6 py-3 text-slate-600">{s.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
